import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Button, type ButtonProps, Spinner, LinkAsButton,
} from '@printdeal/ui-library';
import { FormattedMessage, useIntl } from 'react-intl';
import { ErrorManagement } from '@printdeal/error-management/react';
import canPayOrderHelper from './CanPayOrderHelper';
import { SanitizedOrder } from '../../../../../../types/order/Order';
import { dispatchAccountEvent, DispatchCategoriesAccount } from '../../../../../../helpers/dataLayerHelpers/accountDataLayerHelper';
import { ServiceFactory } from '../../../../../../services/ServiceFactory';

interface PayOrderButtonProps {
  order: SanitizedOrder;
  eventName: 'start_payment_top' | 'start_payment_bottom' | 'click_payment_link';
  variant?: ButtonProps['variant'];
  type?: 'button' | 'link';
  category?: DispatchCategoriesAccount;
}

type PaymentLinkStatuses = 'idle' | 'fetching' | 'available' | 'error';

const PaymentOrderButton = ({
  order,
  eventName = 'click_payment_link',
  variant = 'primary',
  type = 'button',
  category = 'order_detail_interaction',
}: PayOrderButtonProps) => {
  const intl = useIntl();
  const [paymentLinkStatus, setPaymentLinkStatus] = useState<PaymentLinkStatuses>('idle');
  const queryClient = useQueryClient();
  const isOrderPayable = canPayOrderHelper(order);

  const handlePayment = async () => {
    setPaymentLinkStatus('fetching');
    dispatchAccountEvent(category, eventName);

    // PaymentLinks cannot be prefetched because:
    // 1. they expire after a certain amount of time, and...
    // 2. we don't want to create unused payment links in buckeroo.
    const paymentLink = await queryClient.fetchQuery({
      queryKey: ['order', 'get-payment-link', order.id],
      queryFn: async () => {
        try {
          const paymentService = ServiceFactory.createPaymentV2Service();
          return await paymentService.getPaymentInformationForOrder(
            order.orderNumber,
            intl.locale,
          );
        } catch (error) {
          ErrorManagement.exception({
            exception: error,
            tags: {
              fileLocation: 'OrderDetailPagePayOrderButton',
              featureFunction: 'handlePayment',
            },
            fingerprint: ['my-account-order-payment-button'],
          });

          dispatchAccountEvent('order_detail_api_failure', 'error_get_payment_link');
          return '';
        }
      },
    });

    if (paymentLink) {
      setPaymentLinkStatus('available');
      window.location.href = paymentLink;
    } else {
      setPaymentLinkStatus('error');
    }
  };

  if (paymentLinkStatus === 'error') {
    return (
      <Button
        disabled
        variant={variant}
      >
        <FormattedMessage id="account.order.payment.no.link" defaultMessage="Payment link not available" />
      </Button>
    );
  }

  if (!isOrderPayable.enabled) {
    return null;
  }

  if (type === 'link') {
    return (
      <LinkAsButton
        data-cy="doPaymentButton"
        onClick={handlePayment}
        disabled={paymentLinkStatus === 'fetching'}
        isLoading={paymentLinkStatus === 'fetching'}
      >
        { isOrderPayable.isPaymentAgreed ? (
          <FormattedMessage id="account.order.payment.paymentAgreed" defaultMessage="Pay now" />
        ) : (
          <FormattedMessage id="account.order.payment.doPayment" defaultMessage="Payment link" />
        )}
      </LinkAsButton>
    );
  }

  return (
    <Button
      data-cy="doPaymentButton"
      onClick={handlePayment}
      variant={isOrderPayable.isPaymentAgreed ? 'secondary' : variant}
      disabled={paymentLinkStatus === 'fetching'}
    >
      {paymentLinkStatus === 'fetching' && (
        <Spinner
          variant="dim"
          size="sm"
          screenReaderLabel={intl.formatMessage({ id: 'uilib.spinner.loading', defaultMessage: 'Loading...' })}
        />
      )}
      { isOrderPayable.isPaymentAgreed ? (
        <FormattedMessage id="account.order.payment.paymentAgreed" defaultMessage="Pay now" />
      ) : (
        <FormattedMessage id="account.order.payment.doPayment" defaultMessage="Payment link" />
      )}
    </Button>
  );
};

export default PaymentOrderButton;
